import { apiFetch, type ApiFetchOptions } from './client'
import { isApiRecord, type ApiRecord, type ApiValue } from './responseDecoders'

export type BillingCadenceMonths = 1 | 3 | 6 | 12

export interface BillingContract {
  contractId: string
  teamId: string
  startsOn: string
  endsOn: string | null
  periodMonths: BillingCadenceMonths
  timezone: string
  createdBy: string | null
  endedBy: string | null
  updatedBy: string | null
  createdAt: string
  updatedAt: string
  hasClosedPeriod: boolean
}

export interface BillingContractTerms {
  startsOn: string
  endsOn: string | null
  periodMonths: BillingCadenceMonths
  timezone: string
}

export class BillingContractRequestError extends Error {
  readonly status: number
  readonly code: string | null

  constructor(status: number, body: ApiValue) {
    const detail = isApiRecord(body) && isApiRecord(body.detail) ? body.detail : null
    const rawCode = detail?.code
    super(`billing contract request failed: ${status}${typeof rawCode === 'string' ? ` (${rawCode})` : ''}`)
    this.name = 'BillingContractRequestError'
    this.status = status
    this.code = typeof rawCode === 'string' ? rawCode : null
  }
}

function record(raw: ApiValue, context: string): ApiRecord {
  if (!isApiRecord(raw)) throw new Error(`${context} must be an object`)
  return raw
}

function stringField(value: ApiRecord, key: string, context: string): string {
  const field = value[key]
  if (typeof field !== 'string' || field === '') throw new Error(`${context}.${key} must be a string`)
  return field
}

function nullableString(value: ApiRecord, key: string, context: string): string | null {
  const field = value[key]
  if (field === null) return null
  if (typeof field !== 'string' || field === '') throw new Error(`${context}.${key} must be a string or null`)
  return field
}

function cadence(value: ApiValue, context: string): BillingCadenceMonths {
  if (value !== 1 && value !== 3 && value !== 6 && value !== 12) {
    throw new Error(`${context}.period_months must be 1, 3, 6, or 12`)
  }
  return value
}

export function decodeBillingContract(raw: ApiValue, context = 'billing contract'): BillingContract {
  const value = record(raw, context)
  if (typeof value.has_closed_period !== 'boolean') {
    throw new Error(`${context}.has_closed_period must be a boolean`)
  }
  return {
    contractId: stringField(value, 'contract_id', context),
    teamId: stringField(value, 'team_id', context),
    startsOn: stringField(value, 'starts_on', context),
    endsOn: nullableString(value, 'ends_on', context),
    periodMonths: cadence(value.period_months, context),
    timezone: stringField(value, 'timezone', context),
    createdBy: nullableString(value, 'created_by', context),
    endedBy: nullableString(value, 'ended_by', context),
    updatedBy: nullableString(value, 'updated_by', context),
    createdAt: stringField(value, 'created_at', context),
    updatedAt: stringField(value, 'updated_at', context),
    hasClosedPeriod: value.has_closed_period,
  }
}

export function decodeBillingContractList(raw: ApiValue): BillingContract[] {
  const value = record(raw, 'billing contract list')
  if (!Array.isArray(value.contracts)) throw new Error('billing contract list.contracts must be an array')
  return value.contracts.map((item, index) => decodeBillingContract(item, `billing contract list.contracts[${index}]`))
}

const JSON_MUTATION: ApiFetchOptions = {
  retryTransient: false,
  headers: { 'Content-Type': 'application/json' },
}

async function readError(response: Response): Promise<BillingContractRequestError> {
  const body: ApiValue = await response.json().catch(() => null)
  return new BillingContractRequestError(response.status, body)
}

async function decodeResponse<T>(response: Response, decode: (raw: ApiValue) => T): Promise<T> {
  if (!response.ok) throw await readError(response)
  const raw: ApiValue = await response.json()
  return decode(raw)
}

function body(terms: BillingContractTerms): string {
  return JSON.stringify({
    starts_on: terms.startsOn,
    ends_on: terms.endsOn,
    period_months: terms.periodMonths,
    timezone: terms.timezone,
  })
}

export async function listBillingContracts(teamId: string): Promise<BillingContract[]> {
  const response = await apiFetch(`/admin/onboarding/teams/${encodeURIComponent(teamId)}/billing-contracts`)
  return decodeResponse(response, decodeBillingContractList)
}

export async function createBillingContract(teamId: string, terms: BillingContractTerms): Promise<BillingContract> {
  const response = await apiFetch(`/admin/onboarding/teams/${encodeURIComponent(teamId)}/billing-contracts`, {
    ...JSON_MUTATION,
    method: 'POST',
    body: body(terms),
  })
  return decodeResponse(response, (raw) => decodeBillingContract(raw, 'create billing contract response'))
}

export async function updateBillingContract(teamId: string, contractId: string, terms: BillingContractTerms): Promise<BillingContract> {
  const response = await apiFetch(`/admin/onboarding/teams/${encodeURIComponent(teamId)}/billing-contracts/${encodeURIComponent(contractId)}`, {
    ...JSON_MUTATION,
    method: 'PATCH',
    body: body(terms),
  })
  return decodeResponse(response, (raw) => decodeBillingContract(raw, 'update billing contract response'))
}
